/**
 * features/finance/PettyCash.tsx
 * ------------------------------------------------------------------
 * THE TIN, AND WHETHER IT ADDS UP.
 *
 * A school spends real money in small amounts — a taxi for a sick child,
 * a plumber's part, printing for an event — and none of it belongs in
 * the voucher chain. Three signatures for P80 is a control nobody
 * follows, and a control nobody follows means the spending happens
 * anyway, in cash, with nothing recording it.
 *
 * THE SCREEN LEADS ON THE IDENTITY, not on a list of slips:
 *
 *     cash in the tin  +  slips not yet reimbursed  =  the float
 *
 * That one line is the whole control. The float is FIXED and is restored
 * by claiming back exactly what was spent, never topped up by a round
 * number — because an ad-hoc top-up is precisely what lets a missing
 * note disappear without the sum ever failing.
 *
 * A COUNT IS THE POINT OF THE WHOLE THING. Records that are never
 * checked against the cash are just a story about the cash. So counting
 * is a first-class action, an overage is reported as loudly as a
 * shortage — both mean the records and the money disagree — and the
 * variance is stored rather than corrected, because a recurring small
 * shortage is a pattern and a corrected one is invisible.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Wallet, Plus, Calculator, HandCoins, AlertTriangle, CheckCircle2,
  Receipt, Undo2, X, ScrollText,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { db } from '@/lib/firebase';
import {
  PETTY_COLLECTIONS, floatState, reconcileCount, imprestCheck, canSpend,
  type PettyFloat, type PettySlip, type PettyCount,
} from '@/lib/pettyCash';
import {
  createFloat, recordSlip, reverseSlip, reimburse, recordCount,
} from '@/features/finance/pettyWrite';
import { useGlData } from '@/features/finance/useGlData';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
  modalBackdrop, modalSurface,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PettyCash() {
  const { userData } = useAuth();
  const [floats, setFloats] = useState<PettyFloat[] | null>(null);
  const [slips, setSlips] = useState<PettySlip[]>([]);
  const [counts, setCounts] = useState<PettyCount[]>([]);
  const [floatId, setFloatId] = useState('');
  const [modal, setModal] = useState<'slip' | 'count' | 'float' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, PETTY_COLLECTIONS.floats)),
    (s) => setFloats(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettyFloat)),
    () => setFloats([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, PETTY_COLLECTIONS.slips)),
    (s) => setSlips(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettySlip)),
    () => setSlips([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, PETTY_COLLECTIONS.counts)),
    (s) => setCounts(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettyCount)),
    () => setCounts([]),
  ), []);

  const actor = { uid: userData?.uid ?? '', name: userData?.displayName };
  const active = (floats ?? []).filter((f) => f.active !== false);
  const float = active.find((f) => f.id === floatId) ?? active[0];

  const mySlips = useMemo(
    () => slips.filter((s) => s.floatId === float?.id)
      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [slips, float],
  );
  const myCounts = useMemo(
    () => counts.filter((c) => c.floatId === float?.id)
      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [counts, float],
  );
  const state = useMemo(
    () => (float ? floatState(float, mySlips, myCounts) : null),
    [float, mySlips, myCounts],
  );

  async function run(fn: () => Promise<unknown>) {
    setErr(null);
    try { await fn(); setModal(null); } catch (e) { setErr((e as Error).message); }
  }

  if (floats === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Wallet} tone="navy"
        title="Petty Cash"
        subtitle="The tin, on the imprest system — cash plus unclaimed slips equals the float"
        actions={(
          <div className="flex gap-2">
            {float && (
              <>
                <button type="button" className={btnPrimary} onClick={() => setModal('slip')}>
                  <Plus size={15} /> Record a slip
                </button>
                <button type="button" className={btnGhost} onClick={() => setModal('count')}>
                  <Calculator size={15} /> Count the tin
                </button>
              </>
            )}
            <button type="button" className={btnGhost} onClick={() => setModal('float')}>
              Open a float
            </button>
          </div>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      {active.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-gray-500 text-center py-6">
            No float has been opened. A tin without a named custodian is a balance nobody is
            accountable for, so opening one starts by naming who holds it.
          </p>
        </SoftCard>
      ) : (
        <>
          {active.length > 1 && (
            <select value={float?.id ?? ''} onChange={(e) => setFloatId(e.target.value)}
              className={glassFieldClass('mb-4 max-w-sm')}>
              {active.map((f) => (
                <option key={f.id} value={f.id}>{f.name} — {f.custodianName}</option>
              ))}
            </select>
          )}

          {float && state && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
                <StatChip tone="navy" icon={Wallet} value={money(state.cashOnHand)}
                  label="Should be in the tin" />
                <StatChip tone="slate" value={money(state.unreimbursedTotal)}
                  label={`Slips not yet claimed (${state.unreimbursedCount})`} />
                <StatChip tone={state.lowOnCash ? 'amber' : 'slate'}
                  value={`${state.spentPct}%`} label="Of the float spent" />
                <StatChip tone="slate" value={money(state.floatAmount)} label="The float" />
              </div>

              {/* The identity, shown as working rather than asserted. */}
              <SoftCard className="mb-5">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
                  The imprest check
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-200 tabular-nums">
                  <strong>{money(state.cashOnHand)}</strong> in the tin
                  {' + '}<strong>{money(state.unreimbursedTotal)}</strong> in unclaimed slips
                  {state.countVariance !== 0 && (
                    <> {state.countVariance < 0 ? '+' : '−'}{' '}
                      <strong>{money(Math.abs(state.countVariance))}</strong>{' '}
                      {state.countVariance < 0 ? 'written off short' : 'written on over'}</>
                  )}
                  {' = '}<strong>{money(state.floatAmount)}</strong> float
                </p>
                {state.countVariance !== 0 && (
                  <p className="text-[12px] text-gray-500 mt-1.5">
                    {money(Math.abs(state.countVariance))} has been counted and written off against
                    this float. That money left as an expense, so cash and slips alone will not sum
                    to the float again — which is why it is added back here rather than making the
                    check look permanently broken.
                  </p>
                )}
              </SoftCard>

              {state.lowOnCash && (
                <SoftCard className="mb-5 border-l-4 border-amber-400">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      {state.spentPct}% of the float is spent. Claim back{' '}
                      <strong>{money(state.reimbursementDue)}</strong> to restore it — exactly what
                      was spent, never a round number.
                    </p>
                    <button type="button" className={btnPrimary}
                      onClick={() => run(async () => {
                        const r = await reimburse(float.id, mySlips, actor);
                        window.alert(`Claim raised for ${money(r.total)}. `
                          + 'Raise a voucher to draw it from the bank.');
                      })}>
                      <HandCoins size={15} /> Claim {money(state.reimbursementDue)}
                    </button>
                  </div>
                </SoftCard>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2">
                  <SoftCard pad="p-0">
                    <PanelHeader icon={Receipt} title="Slips" tone="navy"
                      subtitle="Never edited — a correction is a reversing slip, so both stay visible" />
                    {mySlips.length === 0 ? (
                      <p className="p-6 text-sm text-gray-500 text-center">Nothing spent yet.</p>
                    ) : (
                      <div className="overflow-x-auto max-h-[440px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white dark:bg-gray-800">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                              <th className="py-2 pl-4 pr-2 font-semibold">Slip</th>
                              <th className="px-2 font-semibold">What for</th>
                              <th className="px-2 font-semibold text-right">Amount</th>
                              <th className="px-2 pr-4 w-8" />
                            </tr>
                          </thead>
                          <tbody>
                            {mySlips.map((s) => {
                              const dead = Boolean(s.reversedBy || s.reversesId);
                              return (
                                <tr key={s.id} className={`border-b border-gray-50 dark:border-gray-800 ${dead ? 'opacity-50' : ''}`}>
                                  <td className="py-2 pl-4 pr-2">
                                    <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{s.slipNo}</span>
                                    <span className="block text-[11px] text-gray-400">{s.date}</span>
                                  </td>
                                  <td className="px-2">
                                    <span className="text-gray-800 dark:text-gray-100">{s.description}</span>
                                    <span className="block text-[11px] text-gray-400">
                                      {s.paidTo}
                                      {!s.hasReceipt && (
                                        <span className="ml-1 text-amber-600">· no receipt</span>
                                      )}
                                      {s.reimbursementId && <span className="ml-1">· claimed</span>}
                                    </span>
                                  </td>
                                  <td className={`px-2 text-right tabular-nums ${s.amount < 0 ? 'text-emerald-700' : ''}`}>
                                    {money(s.amount)}
                                  </td>
                                  <td className="px-2 pr-4">
                                    {!dead && !s.reimbursementId && (
                                      <button type="button" title="Reverse this slip"
                                        className="text-gray-400 hover:text-red-600"
                                        onClick={() => {
                                          const why = window.prompt(`Why is ${s.slipNo} being reversed?`);
                                          if (why) run(() => reverseSlip(s, why, actor));
                                        }}>
                                        <Undo2 size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </SoftCard>
                </div>

                <SoftCard pad="p-0" className="h-fit">
                  <PanelHeader icon={ScrollText} title="Counts" tone="navy"
                    subtitle="Variances kept, not corrected" />
                  {myCounts.length === 0 ? (
                    <p className="p-5 text-sm text-gray-500">
                      The tin has never been counted. Records that are never checked against the
                      cash are only a story about the cash.
                    </p>
                  ) : (
                    <div className="p-4 space-y-2">
                      {myCounts.map((c) => (
                        <div key={c.id} className="text-[13px] border-b border-gray-50 dark:border-gray-800 pb-2 last:border-0">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">{c.date}</span>
                            <span className={`font-medium tabular-nums ${
                              Math.abs(c.variance) < 0.005 ? 'text-emerald-700'
                                : c.variance < 0 ? 'text-red-700' : 'text-amber-700'
                            }`}>
                              {Math.abs(c.variance) < 0.005 ? 'agreed' : money(c.variance)}
                            </span>
                          </div>
                          <p className="text-gray-500 text-[11px]">
                            counted {money(c.countedAmount)} against {money(c.expectedAmount)}
                          </p>
                          {c.explanation && (
                            <p className="text-gray-600 dark:text-gray-300 mt-0.5">{c.explanation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </SoftCard>
              </div>
            </>
          )}
        </>
      )}

      {modal === 'slip' && float && state && (
        <SlipModal float={float} state={state} slips={mySlips} counts={myCounts}
          actor={actor} onClose={() => setModal(null)} onRun={run} />
      )}
      {modal === 'count' && float && state && (
        <CountModal float={float} state={state} slips={mySlips} counts={myCounts}
          actor={actor} onClose={() => setModal(null)} onRun={run} />
      )}
      {modal === 'float' && (
        <FloatModal actor={actor} onClose={() => setModal(null)} onRun={run} />
      )}
    </div>
  );
}

/* -------------------------------- modals -------------------------------- */

function SlipModal({
  float, state, slips, counts, actor, onClose, onRun,
}: {
  float: PettyFloat;
  state: ReturnType<typeof floatState>;
  slips: PettySlip[];
  counts: PettyCount[];
  actor: { uid: string; name?: string };
  onClose: () => void;
  onRun: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const { accounts } = useGlData(`FY${new Date().getFullYear()}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [hasReceipt, setHasReceipt] = useState(true);

  const verdict = canSpend(state, Number(amount) || 0);
  const expenses = accounts.filter((a) => a.type === 'expense');

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-lg w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">Record a slip</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Amount</span>
              <input type="number" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={glassFieldClass('mt-1 text-right')} />
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">What was it for</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              className={glassFieldClass('mt-1')} placeholder="Taxi for a sick pupil" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Paid to</span>
              <input value={paidTo} onChange={(e) => setPaidTo(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Expense account</span>
              <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)}
                className={glassFieldClass('mt-1')}>
                <option value="">— choose —</option>
                {expenses.map((a) => (
                  <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Recorded, not enforced. Refusing a slip with no receipt does not
              make the money come back, and it does guarantee the spend goes
              unrecorded entirely. */}
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={hasReceipt}
              onChange={(e) => setHasReceipt(e.target.checked)} />
            A receipt or till slip was handed in
          </label>

          <p className="text-[12px] text-gray-500">
            {money(state.cashOnHand)} should be in the tin.
          </p>
          {amount && !verdict.ok && (
            <p className="text-[13px] text-red-600">{verdict.reason}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={!verdict.ok}
            onClick={() => onRun(() => recordSlip({
              float, slips, counts, date, description,
              amount: Number(amount), accountCode, paidTo, hasReceipt, actor,
            }))}>
            Record it
          </button>
        </div>
      </div>
    </div>
  );
}

function CountModal({
  float, state, slips, counts, actor, onClose, onRun,
}: {
  float: PettyFloat;
  state: ReturnType<typeof floatState>;
  slips: PettySlip[];
  counts: PettyCount[];
  actor: { uid: string; name?: string };
  onClose: () => void;
  onRun: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [countedAmount, setCountedAmount] = useState('');
  const [explanation, setExplanation] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const counted = Number(countedAmount);
  const result = countedAmount === '' ? null : reconcileCount(state, counted);
  const check = countedAmount === '' ? null : imprestCheck(state, counted);

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-lg w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">Count the tin</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Deliberately does NOT show the expected figure until a number is
              typed. A count that starts by being told the answer is not a count. */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Counted in the tin
              </span>
              <input type="number" step="0.01" value={countedAmount}
                onChange={(e) => setCountedAmount(e.target.value)}
                className={glassFieldClass('mt-1 text-right')} autoFocus />
            </label>
          </div>

          {result && (
            <div className={`rounded-lg border-l-4 p-3 ${
              result.balanced ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                : 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
            }`}>
              <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                {result.balanced
                  ? <CheckCircle2 size={15} className="text-emerald-600" />
                  : <AlertTriangle size={15} className="text-amber-600" />}
                {result.summary}
              </p>
              <p className="text-[12px] text-gray-600 dark:text-gray-300 mt-1 tabular-nums">
                Records say {money(result.expectedAmount)}
                {check && !check.holds && ' — the imprest check does not hold'}
              </p>
            </div>
          )}

          {result && !result.balanced && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                What do you think happened
              </span>
              <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)}
                rows={2} className={glassFieldClass('mt-1')} />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={countedAmount === ''}
            onClick={() => onRun(() => recordCount({
              float, slips, counts, countedAmount: counted, date, explanation, actor,
            }))}>
            Record the count
          </button>
        </div>
      </div>
    </div>
  );
}

function FloatModal({
  actor, onClose, onRun,
}: {
  actor: { uid: string; name?: string };
  onClose: () => void;
  onRun: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const { teachers } = useData();
  const { accounts } = useGlData(`FY${new Date().getFullYear()}`);
  const [name, setName] = useState('');
  const [floatAmount, setFloatAmount] = useState('');
  const [custodianId, setCustodianId] = useState('');
  const [accountCode, setAccountCode] = useState('');

  const cash = accounts.filter((a) => a.type === 'asset');

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-lg w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">Open a float</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className={glassFieldClass('mt-1')} placeholder="Front office tin" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Float amount
              </span>
              <input type="number" step="0.01" value={floatAmount}
                onChange={(e) => setFloatAmount(e.target.value)}
                className={glassFieldClass('mt-1 text-right')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Cash account</span>
              <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)}
                className={glassFieldClass('mt-1')}>
                <option value="">— choose —</option>
                {cash.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">
              Who holds it
            </span>
            <select value={custodianId} onChange={(e) => setCustodianId(e.target.value)}
              className={glassFieldClass('mt-1')}>
              <option value="">— choose —</option>
              {[...teachers].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''))
                .map((t) => <option key={t.id} value={t.id}>{t.displayName}</option>)}
            </select>
          </label>

          <p className="text-[12px] text-gray-500">
            The float is a fixed amount. It is restored by claiming back exactly what was
            spent, never topped up by a round number — an ad-hoc top-up is what lets a
            missing note disappear without the sum ever failing.
          </p>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary}
            onClick={() => onRun(() => createFloat({
              name,
              floatAmount: Number(floatAmount),
              custodianId,
              custodianName: teachers.find((t) => t.id === custodianId)?.displayName ?? '',
              accountCode,
              actor,
            }))}>
            Open it
          </button>
        </div>
      </div>
    </div>
  );
}
